import React, { useEffect, useState, useRef, useCallback } from "react";
import makeRequest from "../../makeRequest";

/* ─── Injected styles ──────────────────────────────────────────── */
const STYLE_ID = "faq-page-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes fFadeUp {
      from { opacity: 0; transform: translateY(28px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes fShimmer {
      0%   { background-position: -600px 0; }
      100% { background-position: 600px 0; }
    }
    @keyframes fSpin {
      to { transform: rotate(360deg); }
    }

    .faq-item {
      background: #fff;
      border-radius: 14px;
      border: 1px solid #e2eaf4;
      box-shadow: 0 4px 18px rgba(10, 44, 88, 0.05);
      overflow: hidden;
      transition: box-shadow 0.25s ease, border-color 0.25s ease;
    }
    .faq-item.open {
      box-shadow: 0 10px 32px rgba(10, 44, 88, 0.11);
      border-color: #a8d4ef;
    }

    .faq-question {
      width: 100%;
      background: none;
      border: none;
      padding: 22px 26px;
      display: flex;
      align-items: center;
      gap: 16px;
      cursor: pointer;
      text-align: left;
      transition: background 0.2s ease;
    }
    .faq-question:hover { background: #f7fafd; }
    .faq-item.open .faq-question { background: #f0f7ff; }

    .faq-num {
      min-width: 36px;
      height: 36px;
      border-radius: 50%;
      background: linear-gradient(135deg, #1b74bb, #0d3b6e);
      color: #fff;
      font-size: 13px;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .faq-question-text {
      flex: 1;
      font-size: 16px;
      font-weight: 600;
      color: #0f1f44;
      line-height: 1.45;
    }

    .faq-chevron {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      background: #edf5fb;
      border: 1px solid #cce3f4;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: #1b74bb;
      font-size: 12px;
      transition: transform 0.32s ease, background 0.2s ease, color 0.2s ease;
    }
    .faq-item.open .faq-chevron {
      transform: rotate(180deg);
      background: #1b74bb;
      border-color: #1b74bb;
      color: #fff;
    }

    .faq-body {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.38s ease;
    }
    .faq-item.open .faq-body { max-height: 600px; }

    .faq-body-divider {
      height: 1px;
      margin: 0 26px;
      background: linear-gradient(to right, #c8def2, transparent);
    }
    .faq-answer {
      padding: 20px 26px 24px 78px;
      font-size: 15px;
      line-height: 1.85;
      color: #4b5e7a;
      margin: 0;
    }
    @media (max-width: 480px) {
      .faq-answer { padding: 18px 20px 20px 20px; }
    }

    .f-shimmer {
      background: linear-gradient(90deg, #e8eef5 0%, #f6f9fc 50%, #e8eef5 100%);
      background-size: 600px 100%;
      animation: fShimmer 1.5s infinite linear;
      border-radius: 8px;
    }

    .faq-spinner {
      width: 36px;
      height: 36px;
      border: 3px solid #dce8f4;
      border-top-color: #1b74bb;
      border-radius: 50%;
      animation: fSpin 0.7s linear infinite;
      margin: 0 auto;
    }

    .f-stat-pill {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      padding: 18px 32px;
      background: rgba(255,255,255,0.09);
      border: 1px solid rgba(255,255,255,0.15);
      border-radius: 16px;
      backdrop-filter: blur(6px);
    }
    .f-stat-pill strong { font-size: 30px; font-weight: 800; color: #fff; line-height: 1; }
    .f-stat-pill span   { font-size: 12px; color: rgba(255,255,255,0.72); margin-top: 4px; font-weight: 500; letter-spacing: 0.5px; }
  `;
  document.head.appendChild(s);
}

/* ─── Single accordion item ────────────────────────────────────── */
function FaqAccordionItem({ item, index, isOpen, onToggle }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.08 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        animation: visible ? `fFadeUp 0.45s ${(index % 15) * 0.05}s ease both` : "none",
      }}
    >
      <div className={`faq-item${isOpen ? " open" : ""}`}>
        <button className="faq-question" onClick={() => onToggle(item.id)} aria-expanded={isOpen}>
          <span className="faq-num">{index + 1}</span>
          <span className="faq-question-text">{item.question}</span>
          <span className="faq-chevron">
            <i className="fa-solid fa-chevron-down" />
          </span>
        </button>
        <div className="faq-body">
          <div className="faq-body-divider" />
          <p className="faq-answer">{item.answer}</p>
        </div>
      </div>
    </div>
  );
}

/* ─── Skeleton item ────────────────────────────────────────────── */
function SkeletonItem({ index }) {
  return (
    <div className="faq-item" style={{ padding: "22px 26px", display: "flex", alignItems: "center", gap: 16 }}>
      <div className="f-shimmer" style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div className="f-shimmer" style={{ height: 15, width: `${55 + (index % 4) * 10}%` }} />
      </div>
      <div className="f-shimmer" style={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0 }} />
    </div>
  );
}

/* ═══ Main ═════════════════════════════════════════════════════════ */
const FaqMain = ({ asSection = false }) => {
  const Tag = asSection ? "div" : "main";
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [openId, setOpenId] = useState(null);
  const sentinelRef = useRef(null);

  const fetchFaqs = useCallback(async (pageNum) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    try {
      const json = await makeRequest({ route: `/website/faq/public?page=${pageNum}`, method: "get" });
      const items = json?.data || [];
      setTotalItems(json?.totalItems || 0);
      setHasMore(json?.next_page != null);
      setFaqs((prev) => (pageNum === 1 ? items : [...prev, ...items]));
    } catch (e) {
      /* silent */
    } finally {
      if (pageNum === 1) setLoading(false);
      else setLoadingMore(false);
    }
  }, []);

  /* initial load */
  useEffect(() => {
    fetchFaqs(1);
  }, [fetchFaqs]);

  /* increment page when sentinel is visible */
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
          setPage((p) => p + 1);
        }
      },
      { threshold: 0.1, rootMargin: "140px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loadingMore, loading]);

  /* fetch next page when page changes */
  useEffect(() => {
    if (page > 1) fetchFaqs(page);
  }, [page, fetchFaqs]);

  const handleToggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <Tag>
      {/* <Breadcrumb title="Frequently Asked Questions" subTitle="FAQ" /> */}

      {/* ── Hero banner ────────────────────────────────────────── */}
      <div
        style={{
          background: "linear-gradient(135deg, #0a1628 0%, #0d3b6e 55%, #1b74bb 100%)",
          padding: "64px 28px 72px",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: "radial-gradient(rgba(255,255,255,0.045) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "-30%",
            right: "-8%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(27,116,187,0.4), transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div style={{ position: "relative", zIndex: 1 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "rgba(255,255,255,0.09)",
              border: "1px solid rgba(255,255,255,0.18)",
              borderRadius: 50,
              color: "#7dd3fc",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 3,
              padding: "7px 22px",
              marginBottom: 20,
              textTransform: "uppercase",
            }}
          >
            <i className="fa-solid fa-circle-question" style={{ fontSize: 12 }} />
            Patient Support
          </span>
          <h1
            style={{
              fontSize: "clamp(26px, 5vw, 50px)",
              fontWeight: 800,
              color: "#fff",
              margin: "0 0 14px",
              lineHeight: 1.15,
            }}
          >
            Frequently Asked Questions
          </h1>
          <p
            style={{
              fontSize: 16,
              color: "rgba(255,255,255,0.65)",
              maxWidth: 520,
              margin: "0 auto 40px",
              lineHeight: 1.85,
            }}
          >
            Find clear, honest answers to the most common questions patients ask us — so you can feel informed and confident before every visit.
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
            {[
              { value: `${totalItems}+`, label: "Questions Answered" },
              { value: "24 / 7", label: "Emergency Care" },
              { value: "100%", label: "Transparency" },
            ].map((stat) => (
              <div key={stat.label} className="f-stat-pill">
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FAQ accordion ──────────────────────────────────────── */}
      <div style={{ background: "#f4f7fb", padding: "72px 0 80px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 28px" }}>
          {/* Section badge */}
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "#edf5fb",
                border: "1.5px solid #bee0ef",
                borderRadius: 50,
                padding: "6px 20px",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 2,
                textTransform: "uppercase",
                color: "#1b74bb",
              }}
            >
              <i className="fa-solid fa-stethoscope" />
              Common Questions
            </span>
          </div>

          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <SkeletonItem key={i} index={i} />
              ))}
            </div>
          ) : faqs.length === 0 ? (
            <div style={{ textAlign: "center", padding: "80px 24px", color: "#94a3b8" }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>❓</div>
              <p style={{ fontSize: 18, fontWeight: 600, color: "#64748b" }}>No FAQs available yet.</p>
              <p style={{ fontSize: 14 }}>Check back soon for answers to common questions.</p>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {faqs.map((item, idx) => (
                  <FaqAccordionItem key={item.id} item={item} index={idx} isOpen={openId === item.id} onToggle={handleToggle} />
                ))}
              </div>

              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} style={{ height: 1, marginTop: 20 }} />

              {loadingMore && (
                <div style={{ paddingTop: 28 }}>
                  <div className="faq-spinner" />
                </div>
              )}

              {!hasMore && faqs.length > 0 && (
                <p
                  style={{
                    textAlign: "center",
                    marginTop: 36,
                    fontSize: 13,
                    color: "#94a3b8",
                    fontStyle: "italic",
                  }}
                >
                  All {faqs.length} question{faqs.length !== 1 ? "s" : ""} loaded.
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── CTA strip ──────────────────────────────────────────── */}
      {!loading && (
        <div
          style={{
            background: "linear-gradient(135deg, #0a1628, #1b74bb)",
            padding: "60px 40px",
            textAlign: "center",
          }}
        >
          <h3
            style={{
              color: "#fff",
              fontSize: "clamp(20px, 2.5vw, 28px)",
              fontWeight: 800,
              margin: "0 0 10px",
            }}
          >
            Still Have Questions?
          </h3>
          <p style={{ color: "rgba(255,255,255,0.68)", fontSize: 15, margin: "0 0 28px" }}>
            Our patient support team is ready to help you every step of the way.
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 14, flexWrap: "wrap" }}>
            <a
              href="/appointment"
              style={{
                display: "inline-block",
                background: "#fff",
                color: "#1b74bb",
                fontWeight: 700,
                fontSize: 14,
                padding: "13px 36px",
                borderRadius: 50,
                textDecoration: "none",
                boxShadow: "0 6px 22px rgba(0,0,0,0.2)",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.05)";
                e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.28)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "0 6px 22px rgba(0,0,0,0.2)";
              }}
            >
              Book an Appointment
            </a>
            <a
              href="/contact"
              style={{
                display: "inline-block",
                background: "rgba(255,255,255,0.1)",
                border: "1.5px solid rgba(255,255,255,0.35)",
                color: "#fff",
                fontWeight: 700,
                fontSize: 14,
                padding: "13px 36px",
                borderRadius: 50,
                textDecoration: "none",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.18)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.1)";
              }}
            >
              Contact Us
            </a>
          </div>
        </div>
      )}
    </Tag>
  );
};

export default FaqMain;
